"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseOrCADNetlist = parseOrCADNetlist;
const NodeParser_1 = require("../../../common/NodeParser");
const BoundaryLevel_1 = require("@cadstrom/sigma-engine-shared/dist/BoundaryLevel");
const OrCADParsingResult_1 = require("../../common/OrCADParsingResult");
const Util_1 = require("../../../Util");
const KNOWN_TOP_LEVEL_NODES = new Set(['description', 'structure', 'placement', 'library', 'network']);
const KNOWN_DESCRIPTION_PROPERTIES = new Set([
    'timeStamp',
    'program',
    'source',
    'title',
    'date',
    'document',
    'revision',
    'organization',
    'address1',
    'address2',
    'address3',
    'address4',
    'partvaluecombine',
    'pcbfootprintcombine',
]);
function parseOrCADNetlist(contents) {
    const context = {
        warnings: [],
    };
    try {
        const rootNode = NodeParser_1.SExpressionFileParser.Parse(contents);
        if (rootNode.name !== 'PCB') {
            context.warnings.push({
                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                message: `Expected root node 'PCB', found '${rootNode.name}'`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                nodeName: rootNode.name,
            });
        }
        const netlist = {
            name: (0, Util_1.removeQuotes)(rootNode.values[0]),
        };
        for (const childNode of rootNode.nodes) {
            switch (childNode.name) {
                case 'description':
                    netlist.description = parseDescription(childNode, context);
                    break;
                case 'structure':
                    netlist.structure = parseStructure(childNode, context);
                    break;
                case 'placement':
                    netlist.placement = parsePlacement(childNode, context);
                    break;
                case 'library':
                    netlist.library = parseLibrary(childNode, context);
                    break;
                case 'network':
                    netlist.network = parseNetwork(childNode, context);
                    break;
                default:
                    if (!KNOWN_TOP_LEVEL_NODES.has(childNode.name)) {
                        context.warnings.push({
                            type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                            message: `Unknown top-level node: ${childNode.name}`,
                            severity: BoundaryLevel_1.BoundaryLevel.Warning,
                            nodeName: childNode.name,
                        });
                    }
                    break;
            }
        }
        const processingResults = {
            type: 'orcad-parsing',
            message: context.warnings.length > 0 ?
                `OrCAD netlist parsing completed with ${context.warnings.length} warning(s)`
                : 'OrCAD netlist parsing completed',
            innerResults: context.warnings,
            severity: context.warnings.length > 0 ? BoundaryLevel_1.BoundaryLevel.Warning : BoundaryLevel_1.BoundaryLevel.Recommendation,
        };
        return {
            netlist,
            processingResults,
        };
    }
    catch (error) {
        const processingResults = {
            type: 'orcad-parsing',
            message: `Failed to parse OrCAD netlist: ${error instanceof Error ? error.message : 'Unknown error'}`,
            innerResults: [],
            severity: BoundaryLevel_1.BoundaryLevel.Error,
        };
        return {
            netlist: { name: '' },
            processingResults,
        };
    }
}
function parseDescription(node, context) {
    const description = {
        properties: {},
    };
    for (const childNode of node.nodes) {
        switch (childNode.name) {
            case 'timeStamp':
                description.timeStamp = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'program':
                description.program = parseProgram(childNode, context);
                break;
            case 'source':
                description.source = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'title':
                description.title = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'date':
                description.date = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'document':
                description.document = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'revision':
                description.revision = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'organization':
                description.organization = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'address1':
                description.address1 = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'address2':
                description.address2 = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'address3':
                description.address3 = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'address4':
                description.address4 = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'partvaluecombine':
                description.partvaluecombine = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            case 'pcbfootprintcombine':
                description.pcbfootprintcombine = (0, Util_1.removeQuotes)(childNode.values[0]);
                break;
            default:
                if (childNode.values.length > 0) {
                    const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
                    const value = (0, Util_1.removeQuotes)(childNode.values[0]);
                    description.properties[key] = value;
                }
                else {
                    const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
                    description.properties[key] = true;
                }
                if (!KNOWN_DESCRIPTION_PROPERTIES.has(childNode.name)) {
                    context.warnings.push({
                        type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownProperty,
                        message: `Unknown property in description: ${childNode.name}`,
                        severity: BoundaryLevel_1.BoundaryLevel.Warning,
                        propertyName: childNode.name,
                    });
                }
                break;
        }
    }
    return description;
}
function parseProgram(node, context) {
    const program = {
        name: (0, Util_1.removeQuotes)(node.values[0]),
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'Version') {
            program.version = (0, Util_1.removeQuotes)(childNode.values[0]);
        }
    }
    return program;
}
function parseStructure(node, context) {
    const structure = {
        properties: {},
    };
    for (const childNode of node.nodes) {
        if (childNode.values.length > 0) {
            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
            const value = (0, Util_1.removeQuotes)(childNode.values[0]);
            structure.properties[key] = value;
        }
        else {
            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
            structure.properties[key] = true;
        }
    }
    return structure;
}
function parsePlacement(node, context) {
    const placement = {
        components: [],
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'component') {
            const component = parseComponent(childNode, context);
            placement.components.push(component);
        }
        else {
            context.warnings.push({
                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                message: `Unknown node in placement: ${childNode.name}`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                nodeName: childNode.name,
            });
        }
    }
    return placement;
}
function parseComponent(node, context) {
    const component = {
        name: (0, Util_1.removeQuotes)(node.values[0]),
        places: [],
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'place') {
            const place = parsePlace(childNode, context);
            component.places.push(place);
        }
        else {
            context.warnings.push({
                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                message: `Unknown node in component ${component.name}: ${childNode.name}`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                nodeName: childNode.name,
                componentName: component.name,
            });
        }
    }
    return component;
}
function parsePlace(node, context) {
    const place = {
        refdes: (0, Util_1.removeQuotes)(node.values[0]),
        properties: {},
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'property') {
            parseProperties(childNode, place.properties, context);
        }
        else {
            context.warnings.push({
                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                message: `Unknown node in place ${place.refdes}: ${childNode.name}`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                nodeName: childNode.name,
                refdes: place.refdes,
            });
        }
    }
    return place;
}
function parseProperties(node, properties, context) {
    for (const childNode of node.nodes) {
        const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
        if (childNode.values.length > 0) {
            const rawValue = (0, Util_1.removeQuotes)(childNode.values[0]);
            const numericValue = parseFloat(rawValue);
            if (!isNaN(numericValue) && isFinite(numericValue) && rawValue.trim() === numericValue.toString()) {
                properties[key] = numericValue;
            }
            else {
                properties[key] = rawValue;
            }
        }
        else {
            properties[key] = true;
        }
    }
}
function parseLibrary(node, context) {
    const library = {
        entries: [],
        properties: {},
    };
    for (const childNode of node.nodes) {
        if (childNode.values.length > 0) {
            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
            const value = (0, Util_1.removeQuotes)(childNode.values[0]);
            library.properties[key] = value;
        }
        else {
            const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
            library.properties[key] = true;
        }
    }
    return library;
}
function parseNetwork(node, context) {
    const nets = [];
    const network = {
        nets,
        properties: {},
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'net') {
            const net = parseNetworkNet(childNode, context);
            if (net) {
                nets.push(net);
            }
        }
        else {
            if (childNode.values.length > 0) {
                const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
                const value = (0, Util_1.removeQuotes)(childNode.values[0]);
                network.properties[key] = value;
            }
            else {
                const key = (0, Util_1.removeQuotes)(childNode.name) || childNode.name;
                network.properties[key] = true;
            }
        }
    }
    return network;
}
function parseNetworkNet(node, context) {
    if (node.name !== 'net') {
        throw new Error(`Expected net node, found ${node.name}`);
    }
    if (node.values.length !== 1) {
        context.warnings.push({
            type: OrCADParsingResult_1.OrCADParsingMessageType.ParsingWarning,
            message: `Invalid net definition: ${node.values.join(' ')}`,
            severity: BoundaryLevel_1.BoundaryLevel.Warning,
        });
        return;
    }
    const net = {
        name: (0, Util_1.removeQuotes)(node.values[0]),
        pins: [],
        properties: {},
    };
    for (const childNode of node.nodes) {
        if (childNode.name === 'pins') {
            net.pins.push(...parseNetworkPins(childNode, context));
        }
        else if (childNode.name === 'property') {
            parseProperties(childNode, net.properties, context);
        }
        else {
            context.warnings.push({
                type: OrCADParsingResult_1.OrCADParsingMessageType.UnknownNode,
                message: `Unknown node in net ${net.name}: ${childNode.name}`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
                nodeName: childNode.name,
                netName: net.name,
            });
        }
    }
    return net;
}
function parseNetworkPins(node, context) {
    if (node.name !== 'pins') {
        throw new Error(`Expected pin node, found ${node.name}`);
    }
    return node.values
        .map((pinStr) => {
        const firstIndex = pinStr.indexOf('-');
        if (firstIndex === -1) {
            context.warnings.push({
                type: OrCADParsingResult_1.OrCADParsingMessageType.ParsingWarning,
                message: `Invalid pin definition: ${pinStr}, missing '-' separator`,
                severity: BoundaryLevel_1.BoundaryLevel.Warning,
            });
            return;
        }
        const referenceDesignator = pinStr.substring(0, firstIndex);
        let pin = pinStr.substring(firstIndex + 1);
        if (pin.startsWith('"') && pin.endsWith('"')) {
            pin = pin.substring(1, pin.length - 1);
        }
        return {
            referenceDesignator,
            pin,
        };
    })
        .filter((pin) => pin !== undefined);
}
//# sourceMappingURL=ONLParser.js.map